/* eslint-disable @typescript-eslint/no-explicit-any */
import {AfterContentChecked, Component, DestroyRef, DoCheck, HostListener, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {CdkDrag, CdkDragHandle, DragRef, Point} from '@angular/cdk/drag-drop';
import {CdkConnectedOverlay, CdkOverlayOrigin} from '@angular/cdk/overlay';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormsModule, NgForm} from '@angular/forms';
import {NgSelectModule} from '@ng-select/ng-select';
import {ColumnMode, DatatableComponent, NgxDatatableModule, SelectionType, SortType} from '@siemens/ngx-datatable';
import {AttributeViewerContext} from '../../../attribute-viewer-context.model';
import {NlrPermissionsDisabledPipe} from '../../../../../../common/pipe/nlr-permissions-disabled-pipe';
import {NlrHasPermissionsPipe} from '../../../../../../common/pipe/nlr-has-permissions.pipe';
import {NlrPatternDirective} from '../../../../../../common/directive/nlr-pattern.directive';
import {MaxLengthInputLimitDirective} from '../../../../../../common/directive/max-length-input-limit.directive';
import {NlrFormFieldHasErrorPipe} from '../../../../../../common/pipe/nlr-form-field-has-error.pipe';
import {NlrTypeheadDirective} from '../../../../../../common/directive/nlr-typehead.directive';
import {FeatureViewerItemLookup} from '../../../../../../common/models/feature-viewer-item.model';
import {AttributeViewerService} from '../../../attribute-viewer.service';
import {AttributeViewerColumnService} from '../../../attribute-viewer-column.service';
import {AttributeViewerFilterService} from '../../../attribute-viewer-filter.service';
import {AttributeViewerEditorService} from '../../../attribute-viewer-editor.service';
import {MetaData, NgEventBus} from 'ng-event-bus';
import {MapToolsService} from '../../../../../services/map-tools.service';
import {PlanModelService} from '../../../../../../common/service/plan-model.service';
import {SpatialUtilService} from '../../../../../services/spatial-util.service';
import {catchError, debounceTime, distinctUntilChanged, EMPTY, first, map, Observable, tap} from 'rxjs';
import {equals} from '../../../../../../common/service/data-util';
import {MapEventsSelectionService} from '../../../../../services/map-events-selection.service';
import {LookupService} from '../../../../../../common/service/lookup.service';
import {LegalParcelTypeLookup} from '../../../../../../common/models/legal-parcel-type.model';
import {RoadTypeLookup} from '../../../../../../common/models/road-type.model';
import {ParcelTypeLookup} from '../../../../../../common/models/parcel-type.model';
import {MapEventsService} from '../../../../../services/map-events.service';
import {MapService} from '../../../../../services/map.service';
import {AlertModalService} from '../../../../../../common/component/modal';
import {InterestService} from '../../../../../services/features/interest.service';
import {InterestReconciliationService} from '../../../../../../common/service/interest-reconciliation.service';
import {LotService} from '../../../../../services/features/lot.service';
import {FeatureAssociationService} from '../../../../../services/features/feature-association.service';
import {PermissionsService} from '../../../../../../common/service/permissions.service';
import {switchMap} from 'rxjs/operators';
import {SelectionUtilityService} from '../../../../../services/selection-utility.service';
import {NlrForbiddenValuesDirective} from '../../../../../../common/directive/nlr-forbidden-values.directive';
import {WidgetService} from '../../../../../services/widget.service';
import {HELP_LINK_CONTENTS_BASE_URL} from '../../../../../../common/constants';
import {HelpLinkComponent} from '../../../../../../help/help-link.component';

@Component({
    selector: 'nlr-feature-business-table',
    imports: [
        CommonModule,
        FormsModule,
        NlrHasPermissionsPipe,
        NlrPermissionsDisabledPipe,
        NlrPatternDirective,
        MaxLengthInputLimitDirective,
        NgxDatatableModule,
        NlrFormFieldHasErrorPipe,
        NlrTypeheadDirective,
        NlrForbiddenValuesDirective,
        CdkDrag,
        CdkDragHandle,
        CdkOverlayOrigin,
        CdkConnectedOverlay,
        HelpLinkComponent,
        NgSelectModule
    ],
    providers: [CommonModule],
    templateUrl: './feature-business-table.component.html',
    styleUrl: './feature-business-table.component.scss'
})
export class FeatureBusinessTableComponent implements OnInit, OnDestroy, DoCheck, AfterContentChecked, AttributeViewerContext {
    @Input()
    tool: FeatureViewerItemLookup;

    @Input()
    editorConfig: any;

    @Input()
    disabled: boolean;

    @Input()
    isDesignTabActive: boolean;

    @ViewChild('dataTable')
    dataTable: DatatableComponent;

    @ViewChild('editorForm')
    editorForm: NgForm;

    @HostListener('click', ['$event.target'])
    onClick(elt: HTMLElement) {
        if (elt.tagName.toLowerCase() === 'i' && elt.classList.contains('action-button') && elt.classList.contains('remove-child')) {
            const parentId = parseInt(elt.getAttribute('data-parent-id'));
            const childId = parseInt(elt.getAttribute('data-child-id'));
            const childType = elt.getAttribute('data-child-type');
            const toolType = this.tool.type;

            if (toolType === 'road') {
                this.featureAssociationService.saveRoadDissociation(parentId, [childId]).subscribe();
                return;
            }

            const saveDissociationEventName = `save${this.getActiveTool()}Dissociation`;

            if (toolType === 'interest') {
                const interest = this.spatialUtilService.copyGraphic(
                    this.spatialUtilService.getInterests().find((i: any) => i.attributes.id === parentId)
                );

                this.eventBus.cast(saveDissociationEventName, {interest: interest, childId: childId, childType: childType});
            } else if (toolType === 'lot') {
                this.featureAssociationService
                    .saveLotDissociation(parentId, childId, childType)
                    .pipe(
                        first(),
                        switchMap(() => this.attributeViewerService.reloadFeatureAttributes(this)),
                        tap(() => {
                            const {formerTenures, interests} = this.planModelService.getModel();
                            const nonSpatialInterests = this.interestReconciliationService.getNonSpatialInterests(formerTenures, interests);
                            return this.interestService.updateInterestGeometry(nonSpatialInterests);
                        }),
                        tap(() => this.spatialUtilService.unhighlightAllFeatures())
                    )
                    .subscribe();
            }
        }
    }

    title = 'FeatureBusinessTableComponent';

    protected readonly Object = Object;

    protected readonly ColumnMode = ColumnMode;

    protected readonly SelectionType = SelectionType;

    protected readonly SortType = SortType;

    roadTypeLookups: RoadTypeLookup[];

    options = {
        id: 'businessFeatures',
        rowHeight: 30,
        headerHeight: 32,
        scrollbarV: true,
        selectable: true,
        multiSelect: true,
        columns: [],
        columnMode: 'force',
        selectAllCheckboxValue: false,
        singleSelect: false,
        messages: {emptyMessage: 'No features found!'}
    };

    oldSingleSelect: boolean = false;

    sorts: {prop: string; dir: string}[] = [];

    featureLayer = {};

    childrenFeatureLayers: any[] = [];

    features: any[];

    attributes: any[] = [];

    attrsList: any[] = [];

    selectedAttrsList: any[] = [];

    selectedParentAttrsListAtLastCheck: any[] = [];

    editorAttributes = [];

    attributeViewerEditorActiveAtLastCheck = false;

    featuresAssociationActive = false;

    annotationToolActive = false;

    businessAttrs = {
        showWarning: true,
        showError: true,
        errors: [],
        warnings: []
    };

    showFilter = false;

    filterApplied = false;

    hideUnselectedRows = false;

    showColumnSettings = false;

    private featureAssociationWatchValueAtLastCheck: string;

    private initialised = false;

    constructor(
        private alertModalService: AlertModalService,
        private attributeViewerService: AttributeViewerService,
        private attributeViewerColumnService: AttributeViewerColumnService,
        private attributeViewerFilterService: AttributeViewerFilterService,
        private attributeViewerEditorService: AttributeViewerEditorService,
        private destroyRef: DestroyRef,
        private eventBus: NgEventBus,
        private featureAssociationService: FeatureAssociationService,
        private interestService: InterestService,
        private interestReconciliationService: InterestReconciliationService,
        private lookupService: LookupService,
        private lotService: LotService,
        private mapService: MapService,
        private mapEventsService: MapEventsService,
        private mapEventsSelectionService: MapEventsSelectionService,
        private mapToolsService: MapToolsService,
        private permissionsService: PermissionsService,
        private planModelService: PlanModelService,
        private selectionUtilityService: SelectionUtilityService,
        private spatialUtilService: SpatialUtilService,
        private widgetService: WidgetService
    ) {}

    ngOnInit() {
        console.debug(`${this.getTitle()} - ngOnInit`);
        this.attributeViewerEditorService.setContext(this);
        switch (this.tool.type) {
            case 'lot':
                this.featureLayer = this.spatialUtilService.getLotMapLayer();
                this.childrenFeatureLayers = [this.spatialUtilService.getPolygonMapLayer(), this.spatialUtilService.getAffectedLotMapLayer()];
                break;
            case 'affectedLot':
                this.featureLayer = this.spatialUtilService.getAffectedLotMapLayer();
                this.childrenFeatureLayers = [this.spatialUtilService.getLotMapLayer(), this.spatialUtilService.getPolygonMapLayer()];
                break;
            case 'interest':
                this.featureLayer = this.spatialUtilService.getInterestMapLayer();
                this.childrenFeatureLayers = [
                    this.spatialUtilService.getPolygonMapLayer(),
                    this.spatialUtilService.getLotMapLayer(),
                    this.spatialUtilService.getAffectedLotMapLayer(),
                    this.spatialUtilService.getPolylineMapLayer()
                ];
                this.spatialUtilService.refreshMapFeatureLayer('interest');
                break;
            case 'road':
                this.featureLayer = this.spatialUtilService.getRoadMapLayer();
                this.childrenFeatureLayers = [this.spatialUtilService.getPolygonMapLayer(), this.spatialUtilService.getRoadMapLayer()];
                break;
        }

        this.roadTypeLookups = this.lookupService.getLookups('roadType');

        this.eventBus
            .on(`moveTo${this.tool.type}FeaturesExtent`)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                console.debug(`${this.getTitle()} - on - moveTo${this.tool.type}FeaturesExtent`);
                this.attributeViewerService.moveToFeaturesExtentCenter(
                    {featureLayer: this.featureLayer, selectedAttrsList: this.selectedAttrsList},
                    []
                );
            });

        this.eventBus
            .on(`stop${this.tool.type}AnnotationTool`)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                console.debug(`${this.getTitle()} - on - stop${this.tool.type}AnnotationTool`);
                this.stopAnnotationTool();
            });

        this.eventBus
            .on('interestDissociationComplete')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.reloadAttrsList();
            });

        this.eventBus
            .on('featuresUpdated')
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                debounceTime(500),
                tap((meta: MetaData) => {
                    console.debug(`${this.getTitle()} - on - featuresUpdated`);

                    const featureClasses: any[] = meta.data;

                    if (featureClasses && !featureClasses.includes(this.tool.type.toLowerCase())) {
                        return;
                    }

                    this.attributeViewerService.reloadFeatureAttributes(this).subscribe();
                })
            )
            .subscribe();

        this.eventBus
            .on('attributeViewerResized')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.dataTable.recalculate());

        window.setTimeout(() => {
            this.attributeViewerColumnService.populateColumns(this, true);
            this.attributeViewerService
                .reloadFeatureAttributes(this)
                .pipe(
                    tap(() => {
                        this.eventBus
                            .on('featureHighlightsChanged')
                            .pipe(
                                takeUntilDestroyed(this.destroyRef),
                                debounceTime(100),
                                tap(() => {
                                    console.debug('on featureHighlightChanged with this.setDisplayAttrsTimeout feature business');
                                }),
                                tap((meta: MetaData) => {
                                    const oldFeatureHighlightsMap = meta.data;
                                    const oldHighlightedFeatureIds: number[] = oldFeatureHighlightsMap[this.tool.type.toLowerCase()] || [];
                                    const newHighlightedFeatureIds: number[] = this.spatialUtilService.getHighlightedFeatureIds(this.tool.type);
                                    if (
                                        newHighlightedFeatureIds.length === oldHighlightedFeatureIds.length &&
                                        newHighlightedFeatureIds.every(id => oldHighlightedFeatureIds.includes(id))
                                    ) {
                                        return;
                                    }

                                    const highlightedFeatureIds = this.spatialUtilService.getHighlightedFeatureIds(this.featureLayer);
                                    const selectedAttrsListIds = this.selectedAttrsList.map(attrs => attrs.id);

                                    if (
                                        selectedAttrsListIds.length === highlightedFeatureIds.length &&
                                        selectedAttrsListIds.every(featureId => highlightedFeatureIds.includes(featureId))
                                    ) {
                                        return;
                                    }

                                    this.attributeViewerService.unhighlightFeaturesOutOfContextAllowed(false);

                                    this.selectedAttrsList.splice(
                                        0,
                                        this.selectedAttrsList.length,
                                        ...this.attrsList.filter(attrs => highlightedFeatureIds.includes(attrs.id))
                                    );
                                    // update selected items in table
                                    this.dataTable.selected = this.dataTable.rows.filter(row => highlightedFeatureIds.includes(row.id));
                                    this.dataTable.recalculate();
                                })
                            )
                            .subscribe();

                        this.eventBus
                            .on(`select${this.tool.type}Features`)
                            .pipe(
                                takeUntilDestroyed(this.destroyRef),
                                tap((meta: MetaData) => {
                                    console.debug(`${this.getTitle()} - on - select${this.tool.type}Features`);
                                    const features = meta.data;
                                    this.attributeViewerService.unhighlightFeaturesOutOfContextAllowed(false);
                                    this.attributeViewerService.selectFeatures(features, this.attrsList, this.selectedAttrsList);
                                })
                            )
                            .subscribe();

                        this.attrsList = this.features.map(feature => feature.attributes).filter(attrs => !attrs.transientParentId);

                        this.selectedParentAttrsListAtLastCheck = this.getParentAttrsList(this.selectedAttrsList);

                        this.initialised = true;
                    })
                )
                .subscribe();
        }, 0);

        this.eventBus
            .on('AttributeViewer:close')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.stopAnnotationTool();

                this.spatialUtilService.unhighlightFeatures('lot');
                this.spatialUtilService.unhighlightFeatures('affectedlot');
                this.spatialUtilService.unhighlightFeatures('road');
                this.spatialUtilService.unhighlightFeatures('interest');

                this.mapToolsService.deactivate('FEATURE_ASSOCIATION');
            });
    }
    reloadAttrsList() {
        this.attributeViewerService.reloadFeatureAttributes(this).subscribe(updatedList => {
            this.attrsList = [...updatedList];
        });
    }
    ngOnDestroy() {
        this.selectionUtilityService.multiSelectEnabled(false);
    }

    ngDoCheck() {
        if (!this.initialised) {
            return;
        }

        const selectedPolylineIdsString = this.spatialUtilService
            .getHighlightedPolylines()
            .map(p => p.attributes.id)
            .join();
        const selectedPolygonIdsString = this.spatialUtilService
            .getHighlightedPolygons()
            .map((p: any) => p.attributes.id)
            .join();
        const selectedFeatureIdsString = this.selectedAttrsList.map(attrs => attrs.id).join();

        const featureAssociationWatchValue =
            `featuresAssociationActive:${this.featuresAssociationActive}` +
            `|selectedFeatures:${selectedFeatureIdsString}` +
            `|selectedPolygons:${selectedPolygonIdsString}` +
            `|selectedPolylines:${selectedPolylineIdsString}`;

        if (featureAssociationWatchValue !== this.featureAssociationWatchValueAtLastCheck) {
            this.featureAssociationWatchValueAtLastCheck = featureAssociationWatchValue;
            this.validate();
        }

        if (this.oldSingleSelect !== this.options.singleSelect) {
            this.eventBus.cast(this.options.singleSelect ? 'disableTab' : 'enableTab', 'geometryTab');
            this.oldSingleSelect = this.options.singleSelect;
        }

        const selectedParentAttrsList = this.getParentAttrsList(this.selectedAttrsList);
        if (!equals(selectedParentAttrsList, this.selectedParentAttrsListAtLastCheck)) {
            const parentFeatureCount = this.getParentFeatureCount();
            if (this.selectedParentAttrsListAtLastCheck.length != parentFeatureCount) {
                for (let i = this.selectedAttrsList.length - 1; i >= 0; i--) {
                    if (this.selectedAttrsList[i].transientParentId) {
                        this.selectedAttrsList.splice(i, 1);
                    }
                }
            }

            if (!equals(this.selectedAttrsList, this.selectedParentAttrsListAtLastCheck)) {
                this.attributeViewerService.checkSelectedFeaturesChange(this, this.selectedAttrsList, this.selectedParentAttrsListAtLastCheck);

                if (!this.featuresAssociationActive && this.attributeViewerService.unhighlightFeaturesOutOfContextAllowed()) {
                    const newSelectedAttrIds = this.selectedAttrsList.map(attrs => attrs.id);
                    const featureClass = this.spatialUtilService.getFeatureClass(this.featureLayer);

                    this.spatialUtilService.unhighlightFeatures(
                        featureClass,
                        this.spatialUtilService.getFeatureIds(featureClass).filter((featureId: any) => !newSelectedAttrIds.includes(featureId))
                    );

                    if (this.mapEventsSelectionService.enableInterestAnnotationClick) {
                        this.mapEventsSelectionService.enableInterestAnnotationClick = false;
                        this.spatialUtilService
                            .getFeatureClasses()
                            .filter(fc => fc !== featureClass && fc !== 'annotation')
                            .forEach(fc => this.spatialUtilService.unhighlightFeatures(fc));
                    } else {
                        this.spatialUtilService
                            .getFeatureClasses()
                            .filter(fc => fc !== featureClass)
                            .forEach(fc => this.spatialUtilService.unhighlightFeatures(fc));
                    }
                }
            }

            if (this.mapToolsService.isActive('ATTRIBUTE_EDITOR_WINDOW')) {
                this.attributeViewerEditorActiveAtLastCheck = true;
                this.attributeViewerEditorService.setupEditor(this).subscribe();
            }

            this.selectedParentAttrsListAtLastCheck = this.getParentAttrsList(this.selectedAttrsList);
        } else if (this.mapToolsService.isActive('ATTRIBUTE_EDITOR_WINDOW')) {
            if (!this.attributeViewerEditorActiveAtLastCheck) {
                this.attributeViewerEditorActiveAtLastCheck = true;
                this.attributeViewerEditorService.setupEditor(this).subscribe();
            }
        } else {
            this.attributeViewerEditorActiveAtLastCheck = false;
        }
    }

    onClickColumnSettingsWidget() {
        this.widgetService.moveToTop('attributeViewer');
    }

    onClickFilterFeaturesWidget() {
        this.widgetService.moveToTop('attributeViewer');
    }

    onClickAttributeEditor() {
        this.widgetService.moveToTop('attributeViewer');
    }

    computeDragPosition = (userPointerPosition: Point, dragRef: DragRef, dimensions: DOMRect): Point => {
        return this.widgetService.computeDragPosition(userPointerPosition, dragRef, dimensions);
    };

    computeEditorDragPosition = (userPointerPosition: Point, dragRef: DragRef, dimensions: DOMRect): Point => {
        const dragPosition = this.widgetService.computeDragPosition(userPointerPosition, dragRef, dimensions);
        this.editorConfig.style = {
            top: dragPosition.y,
            left: dragPosition.x
        };
        return dragPosition;
    };

    ngAfterContentChecked() {
        this.syncSelectAllCheckbox();
    }

    getActiveTool() {
        return this.tool.type.charAt(0).toUpperCase() + this.tool.type.slice(1);
    }

    getAttributesInDisplayOrder() {
        return this.attributes.sort((a: any, b: any) => a.displayOrder - b.displayOrder);
    }

    getEditorAttributes() {
        return this.editorAttributes.sort((a: any, b: any) => a.editableOrder - b.editableOrder);
    }

    isEditorActive() {
        return this.mapToolsService.isActive('ATTRIBUTE_EDITOR_WINDOW');
    }

    createInterestIsDisabled() {
        return (
            this.planModelService.readonly ||
            this.disabled ||
            this.mapToolsService.isActive('DISPLAY_MODE_PRINT_PREVIEW') ||
            this.permissionsService.isDisabledByPermissions(['IN_DESIGN', 'EDIT_INTEREST', 'EDIT_DESIGN', 'VIEW_DESIGN'])
        );
    }

    openNewInterest() {
        this.attributeViewerService.openEditor(this, false);
    }

    openAttributeEditor() {
        if (this.tool.type === 'interest') {
            if (this.selectedAttrsList.length > 0) {
                this.attributeViewerService.openEditor(this, true);
            } else {
                this.attributeViewerService.openEditor(this, false);
            }
        } else {
            this.attributeViewerService.openEditor(this, true);
        }
    }

    closeEditor() {
        this.mapToolsService.deactivate('ATTRIBUTE_EDITOR_WINDOW');
        this.editorForm.form.markAsPristine();
    }

    saveEditor() {
        this.attributeViewerEditorService
            .saveEditor(this, this.editorForm)
            .pipe(
                first(),
                tap(() => this.eventBus.cast('featuresUpdated'))
            )
            .subscribe();
    }

    getAttributeEditorHelpUrl() {
        const toolTypeKebabCase = this.tool.type
            .split(/(?=[A-Z])/)
            .map(v => v.toLowerCase())
            .join('-');
        return HELP_LINK_CONTENTS_BASE_URL + `attribute-editor-${toolTypeKebabCase}-help.html`;
    }

    getAttributeEditorHelpPageTitle() {
        const toolDescription = {
            affectedLot: 'Affected Lots | Edit',
            interest: 'Interests | Edit',
            lot: 'Lots | Edit',
            road: 'Roads | Edit'
        }[this.tool.type];

        return `Attribute Viewer | ${toolDescription}`;
    }

    getColumnChooserHelpUrl() {
        const toolTypeKebabCase = this.tool.type
            .split(/(?=[A-Z])/)
            .map(v => v.toLowerCase())
            .join('-');
        return HELP_LINK_CONTENTS_BASE_URL + `attribute-viewer-${toolTypeKebabCase}-column-chooser-help.html`;
    }

    getColumnChooserHelpPageTitle() {
        const toolDescription = {
            affectedLot: 'Affected Lots | Column Chooser',
            lot: 'Lots | Column Chooser',
            road: 'Road/Road Widening | Column Chooser',
            interest: 'Interests & Notifications | Column Chooser'
        }[this.tool.type];

        return `Attribute Viewer | ${toolDescription}`;
    }

    getFilterHelpUrl() {
        const toolTypeKebabCase = this.tool.type
            .split(/(?=[A-Z])/)
            .map(v => v.toLowerCase())
            .join('-');
        return HELP_LINK_CONTENTS_BASE_URL + `attribute-viewer-${toolTypeKebabCase}-feature-filter-help.html`;
    }

    getFilterHelpPageTitle() {
        const toolDescription = {
            affectedLot: 'Affected Lots | Filter Features',
            lot: 'Lots | Filter Features',
            road: 'Road/Road Widening | Filter Features',
            interest: 'Interests & Notifications | Filter Features'
        }[this.tool.type];

        return `Attribute Viewer | ${toolDescription}`;
    }

    getAttributeEditorTooltip() {
        return this.mapToolsService.getTooltipWithShortcut('ATTRIBUTE_EDITOR_WINDOW', 'Attribute Editor');
    }

    filterFeaturesByAttributeValue(attribute: any, value: string) {
        const features = this.attributeViewerFilterService.filterFeaturesByAttributeValue(this, attribute, value);
        return features.length > 8 ? features.slice(0, 8) : features;
    }

    filterFeaturesSearch(attribute: any) {
        return (text$: Observable<string>) =>
            text$.pipe(
                debounceTime(200),
                distinctUntilChanged(),
                map(term => (term.length === 0 ? [] : this.filterFeaturesByAttributeValue(attribute, term)))
            );
    }

    applyFilter() {
        this.attributeViewerFilterService.applyFilter(this);
    }

    clearFilter() {
        this.attributeViewerFilterService.clearFilter(this);
    }

    attributeSelectorChanged(attribute: any) {
        this.attributeViewerEditorService.attributeSelectorChanged(this, attribute).subscribe();
    }

    showOrHideColumn(attribute: any) {
        this.attributeViewerColumnService.showOrHideColumn(this.options, attribute);
    }

    toggleAnnotationTool() {
        if (this.annotationToolActive) {
            this.stopAnnotationTool();
        } else {
            this.startAnnotationTool();
        }
    }

    private startAnnotationTool() {
        this.mapToolsService
            .activate(
                'ADD_ADDITIONAL_ANNOTATION',
                () => {
                    this.annotationToolActive = true;
                    this.options.singleSelect = true;

                    this.mapEventsService.deleteMapClickHandler();

                    // ND-304: disable open tools
                    this.eventBus.cast(`add${this.getActiveTool()}AnnotationAssociation`, this.selectedAttrsList[0]);
                },
                () => {
                    if (this.annotationToolActive) {
                        this.stopAnnotationTool();
                    }
                }
            )
            .subscribe();
    }

    private stopAnnotationTool() {
        this.annotationToolActive = false;
        this.options.singleSelect = false;

        this.mapEventsService.resetMapClickHandler(this.spatialUtilService.map);

        this.eventBus.cast(`remove${this.getActiveTool()}AnnotationAssociation`);

        this.mapToolsService.deactivate('ADD_ADDITIONAL_ANNOTATION');
    }

    associationTooltip() {
        switch (this.tool.type) {
            case 'interest':
                return this.selectedAttrsList.length === 1 && !this.selectedAttrsList[0].subject
                    ? 'Linking is not permitted. No subject has been entered.'
                    : '';
            case 'road':
                return this.selectedAttrsList.length === 0 ? 'No roads selected.' : '';
            default:
                return '';
        }
    }

    getAddAnnotationTooltip() {
        switch (this.tool.type) {
            case 'road':
                return 'Add another road name annotation to the plan';
            case 'interest':
                return 'Add another interest annotation to the plan';
            case 'affectedLot':
                return 'Add another affected lot annotation to the plan';
            default:
                return '';
        }
    }

    addAnnotationIsDisabled() {
        if (
            this.planModelService.readonly ||
            this.disabled ||
            this.featuresAssociationActive ||
            this.selectedAttrsList.length === 0 ||
            this.mapService.getMapMode().type !== 'mainPlan' ||
            this.mapToolsService.isSuspended('ATTRIBUTE_VIEWER_WINDOW') ||
            this.selectedAttrsList.length > 1 ||
            this.permissionsService.isDisabledByPermissions(this.tool.permissions)
        ) {
            return true;
        }

        const selectedAttrs = this.selectedAttrsList[0];

        if (
            this.tool.type === 'road' &&
            this.roadTypeLookups.some(p => selectedAttrs.type === p.description && !p.allowedExtraAnnotationToBeAddedToThePlan)
        ) {
            return true;
        }

        if (Object.hasOwn(selectedAttrs, 'affectedtenureid')) {
            if (!selectedAttrs['affectedtenureid']) {
                return true;
            }
        } else {
            // disable if the selected feature has no children
            const childAttrsList = this.getChildAttrsList(selectedAttrs.transientId);
            if (childAttrsList.length === 0) {
                return true;
            }
        }

        // disable if there are no polygon ids
        if (!Object.hasOwn(selectedAttrs, 'affectedtenureid')) {
            if (!selectedAttrs.polygonids && !selectedAttrs.polylineids) {
                return true;
            }
        }

        // disable the feature if there are no Road polygons
        if (this.tool.type === 'road') {
            return !this.spatialUtilService.parseIdsAttr(selectedAttrs.polygonids).some(polygonId => {
                const polygon = this.mapService.findFeatureByTypeAndId(this.spatialUtilService.getPolygonMapLayer(), polygonId);
                return polygon && polygon.attributes.type === 10;
            });
        }

        return false;
    }

    editAttributesIsDisabled() {
        return (
            this.planModelService.readonly ||
            this.disabled ||
            this.options.singleSelect ||
            this.selectedAttrsList.length !== 1 ||
            (this.mapToolsService.isActive('DISPLAY_MODE_PRINT_PREVIEW') && ['affectedLot', 'interest'].includes(this.tool.type)) ||
            (this.tool.type === 'interest' && this.spatialUtilService.interestHasUnsavedAssociations(this.selectedAttrsList[0].id)) ||
            this.permissionsService.isDisabledByPermissions(['EDIT_DESIGN', 'VIEW_DESIGN'])
        );
    }

    getDisabledEditAttributesTooltip() {
        if (
            this.planModelService.readonly ||
            this.disabled ||
            this.options.singleSelect ||
            this.selectedAttrsList.length !== 1 ||
            (this.mapToolsService.isActive('DISPLAY_MODE_PRINT_PREVIEW') && ['affectedLot', 'interest'].includes(this.tool.type))
        ) {
            return '';
        }

        if (this.tool.type === 'interest' && this.spatialUtilService.interestHasUnsavedAssociations(this.selectedAttrsList[0].id)) {
            return 'To edit you must first save the interest association changes';
        }

        return '';
    }

    showStartFeatureAssociation() {
        return this.tool.enableAssociation && (this.annotationToolActive || !this.options.singleSelect);
    }

    showSaveFeatureAssociation() {
        return this.tool.enableAssociation && !this.annotationToolActive && this.options.singleSelect;
    }

    showCancelFeatureAssociation() {
        return this.tool.enableAssociation && !this.annotationToolActive && this.options.singleSelect;
    }

    isSelectAllBurdenedLotPolygons() {
        return (
            this.tool.type === 'interest' &&
            this.showSaveFeatureAssociation() &&
            this.planModelService.getModel().planPurpose.parentTenureAssociationType !== 'AFFECTED_TENURE'
        );
    }

    selectAllBurdenedLotPolygons() {
        const polygonFeatures = [];
        this.selectedAttrsList.forEach(item => {
            if (item.burdenlots) {
                item.burdenlots.split(',').forEach((lot: string) => {
                    this.mapService.findFeaturesByTypeAndAttribute(this.spatialUtilService.getLotMapLayer(), 'name', lot).forEach((localLot: any) => {
                        if (localLot && localLot.attributes.polygonids) {
                            this.spatialUtilService.parseIdsAttr(localLot.attributes.polygonids).forEach((polygonId: number) => {
                                const localPolygon = this.mapService.findFeatureByTypeAndId(this.spatialUtilService.getPolygonMapLayer(), polygonId);
                                polygonFeatures.push(localPolygon);
                            });
                        }
                    });
                });
            }
        });

        this.spatialUtilService.highlightFeatures('polygon', polygonFeatures);
    }

    startFeatureAssociationIcon() {
        switch (this.tool.type) {
            case 'interest':
                return this.selectedAttrsList.length === 0 ||
                    this.getChildFeatureCount(this.selectedAttrsList[0].transientId) === 0 ||
                    !this.selectedAttrsList[0].subject
                    ? 'nlrp-icon-link-features'
                    : 'nlrp-icon-edit-linked-features';
            case 'road':
            case 'lot':
                return this.selectedAttrsList.length === 0 || this.getChildFeatureCount(this.selectedAttrsList[0].transientId) === 0
                    ? 'nlrp-icon-link-features'
                    : 'nlrp-icon-edit-linked-features';
            case 'affectedLot':
                return 'nlrp-icon-link-features';
            default:
                return '';
        }
    }

    startFeaturesAssociation() {
        console.debug(`${this.getTitle()} - startFeaturesAssociation`);

        if (this.tool.type === 'lot' && this.selectedAttrsList.length > 1) {
            this.alertModalService.createAlertMessage(this.tool.errorMessage1, 'Ok');
            return;
        }

        this.mapToolsService
            .activate(
                'FEATURE_ASSOCIATION',
                () => {
                    this.options.singleSelect = true;

                    this.featuresAssociationActive = true;

                    if (this.tool.type === 'interest') {
                        if (this.selectedAttrsList.length > 1) {
                            this.spatialUtilService.unhighlightAllFeatures();
                        }
                        this.eventBus.cast('filterFeatureSelection', true);
                    }

                    this.attributeViewerService.setFeatureHighlights(this, true, this.selectedAttrsList.slice(), false, true);
                },
                () => {
                    if (this.featuresAssociationActive) {
                        this.options.singleSelect = false;

                        this.featuresAssociationActive = false;

                        this.eventBus.cast('filterFeatureSelection', false);

                        this.attributeViewerService.setFeatureHighlights(this, false, this.selectedAttrsList.slice(), false, true);

                        if (this.mapToolsService.isActive('MAP_SELECTION_DRAG') && this.mapToolsService.isSuspended('ZOOM')) {
                            this.mapToolsService.deactivate('MAP_SELECTION_DRAG');
                        }
                    }
                }
            )
            .subscribe(() => {
                this.selectionUtilityService.multiSelectEnabled(true);
            });
    }

    associationIsDisabled() {
        if (
            this.planModelService.readonly ||
            this.disabled ||
            this.getParentFeatureCount() === 0 ||
            this.mapService.getMapMode().type !== 'mainPlan' ||
            (this.tool.type === 'interest' && this.selectedAttrsList.length === 0) ||
            this.selectedAttrsList.length > 1 ||
            this.annotationToolActive ||
            this.mapToolsService.isSuspended('ATTRIBUTE_VIEWER_WINDOW') ||
            this.permissionsService.isDisabledByPermissions(this.tool.permissions)
        ) {
            return true;
        }

        switch (this.tool.type) {
            case 'interest':
                return this.selectedAttrsList.length === 0 || (this.selectedAttrsList.length > 0 && !this.selectedAttrsList[0].subject);
            case 'road':
                return this.selectedAttrsList.length === 0;
            default:
                return false;
        }
    }

    saveAssociationPointerEventsStyle() {
        if (
            (this.tool.type === 'interest' && this.selectedAttrsList.length > 0 && !this.selectedAttrsList[0].subject) ||
            (this.tool.type === 'road' && this.selectedAttrsList.length === 0)
        ) {
            return 'none';
        }
        return '';
    }

    saveFeaturesAssociation() {
        console.debug(`${this.getTitle()} - saveFeaturesAssociation`);

        if (this.selectedAttrsList.length > 0) {
            const parentId = this.selectedAttrsList[0].id;

            if (this.tool.type === 'lot') {
                this.lotService
                    .saveLotAssociation(parentId)
                    .pipe(
                        switchMap(() => {
                            // DCX-7976: update interest boundaries
                            const {formerTenures, interests} = this.planModelService.getModel();
                            return this.interestService.updateInterestGeometry(
                                this.interestReconciliationService.getNonSpatialInterests(formerTenures, interests)
                            );
                        }),
                        tap(() => {
                            this.eventBus.cast('filterFeatureSelection', false);
                            this.eventBus.cast('featuresUpdated');
                            this.options.singleSelect = false;
                            this.attributeViewerService.setFeatureHighlights(this, false, this.selectedAttrsList.slice(), false, true);
                            this.mapToolsService.deactivate('FEATURE_ASSOCIATION');
                            this.reloadAttrsList();
                        })
                    )
                    .subscribe();
            } else if (this.tool.type === 'interest') {
                this.featureAssociationService
                    .saveInterestAssociation(parentId)
                    .pipe(
                        tap(() => {
                            this.eventBus.cast('filterFeatureSelection', false);
                            this.eventBus.cast('featuresUpdated');
                            this.options.singleSelect = false;
                            this.attributeViewerService.setFeatureHighlights(this, false, this.selectedAttrsList.slice(), false, true);
                            this.mapToolsService.deactivate('FEATURE_ASSOCIATION');
                        })
                    )
                    .subscribe();
            } else if (this.tool.type === 'road') {
                this.featureAssociationService
                    .saveRoadAssociation(parentId)
                    .pipe(
                        tap(() => {
                            this.eventBus.cast('filterFeatureSelection', false);
                            this.eventBus.cast('featuresUpdated');
                            this.options.singleSelect = false;
                            this.attributeViewerService.setFeatureHighlights(this, false, this.selectedAttrsList.slice(), false, true);
                            this.mapToolsService.deactivate('FEATURE_ASSOCIATION');
                        }),
                        catchError(error => {
                            this.alertModalService.createAlertMessage(this.tool[error] || error, 'Ok');
                            return EMPTY;
                        })
                    )
                    .subscribe();
            }
        } else {
            this.alertModalService.createAlertMessage(
                `No ${this.tool.type} was selected. Please select a ${this.tool.type} before proceeding.`,
                'Ok'
            );
        }
        this.selectionUtilityService.multiSelectEnabled(false);
    }

    cancelFeaturesAssociation() {
        console.debug(`${this.getTitle()} - cancelFeaturesAssociation`);
        this.mapToolsService.deactivate('FEATURE_ASSOCIATION');
        this.selectionUtilityService.multiSelectEnabled(false);
    }

    isDeleteSelectedFeaturesVisible() {
        return this.tool.type !== 'interest' && this.tool.type !== 'road' && this.tool.type !== 'affectedLot';
    }

    deleteSelectedFeaturesIsDisabled() {
        return (
            this.planModelService.readonly ||
            this.disabled ||
            this.options.singleSelect ||
            this.selectedAttrsList.length === 0 ||
            this.mapToolsService.isActive('DISPLAY_MODE_PRINT_PREVIEW') ||
            this.permissionsService.isDisabledByPermissions(this.tool.permissions)
        );
    }

    deleteSelectedFeatures() {
        this.attributeViewerService.deleteSelectedFeatures(this);
    }

    hideShowUnselectedRows() {
        this.attributeViewerFilterService.hideShowUnselectedRows(this);
    }

    clearAllRows() {
        this.spatialUtilService.unhighlightFeatures(this.featureLayer);
    }

    isDisabled() {
        return this.mapToolsService.isSuspended('ATTRIBUTE_VIEWER_WINDOW') || this.selectedAttrsList.length === 0;
    }

    isRemoveAssociationDisabled() {
        return this.mapToolsService.isSuspended('ATTRIBUTE_VIEWER_WINDOW');
    }

    isAttributeRequired(attribute: any) {
        return (attribute.mandatory && this.selectedAttrsList.length === 1) || (this.selectedAttrsList.length > 1 && !attribute.bulkUpdate);
    }

    getForbiddenValues(attribute: any) {
        if (!attribute.unique || this.selectedAttrsList.length === 0) {
            return [];
        }

        const attrs = this.selectedAttrsList[0];

        return this.features.filter(f => !f.attributes.transientParentId && f.attributes.id !== attrs.id).map(f => `${f.attributes[attribute.prop]}`);
    }

    onSelect(evt: {selected: any[]}) {
        console.log(`${this.getTitle()} - onSelect - selected ids:`, evt.selected.map(attrs => attrs.id).join());

        /// remove any current annotation association
        if (this.annotationToolActive && this.selectedAttrsList.length > 0) {
            this.eventBus.cast(`remove${this.getActiveTool()}AnnotationAssociation`, this.selectedAttrsList[0]);
        }

        // DCX-6162: Disable road annotation tool if selection changes:
        if (this.tool.type === 'road' && this.annotationToolActive) {
            this.stopAnnotationTool();
        }

        // Deselect all rows if evt.selected contains one row that was already selected
        if (
            evt.selected.length === 1 &&
            this.selectedParentAttrsListAtLastCheck.includes(evt.selected[0]) &&
            !this.attributeViewerService.ctrlKeyDown &&
            !this.attributeViewerService.shiftKeyDown
        ) {
            this.selectedAttrsList = [];
            return;
        }

        const featureAttributesArray = this.options.singleSelect ? evt.selected : this.getParentAttrsList(evt.selected);
        const selectedParentIds = featureAttributesArray.filter(attrs => !attrs.transientParentId).map(attrs => attrs.transientId);
        const expandedSelectedParentIds = featureAttributesArray.filter(attrs => attrs.treeStatus === 'expanded').map(attrs => attrs.transientId);

        this.selectedAttrsList.splice(
            0,
            this.selectedAttrsList.length,
            ...this.attrsList.filter(
                attrs => selectedParentIds.includes(attrs.transientId) || expandedSelectedParentIds.includes(attrs.transientParentId)
            )
        );

        /// add a new annotation association
        if (this.annotationToolActive) {
            this.eventBus.cast(`add${this.getActiveTool()}AnnotationAssociation`, this.selectedAttrsList[0]);
        }
    }

    onTreeAction(evt: any) {
        console.log(`${this.getTitle()} - onTreeAction`, evt);

        evt.row.treeStatus = evt.row.treeStatus === 'expanded' ? 'collapsed' : 'expanded';
        this.applyFilter();
        window.setTimeout(() => this.applySorts(), 100);

        // Prevent the row selection state changing
        event.stopPropagation();
    }

    onSort(evt: any) {
        console.log(`${this.getTitle()} - onSort - evt`, evt);

        this.sorts.splice(0, this.sorts.length, ...evt.sorts);

        window.setTimeout(() => this.applySorts(), 100);
    }

    public applySorts() {
        this.attrsList = this.attrsList
            .filter(attrs => attrs.transientId && !attrs.transientParentId)
            .sort((a: any, b: any) => {
                for (const sort of this.sorts) {
                    let aValue = a[sort.prop];
                    if (aValue === null || aValue === undefined) aValue = '';
                    let bValue = b[sort.prop];
                    if (bValue === null || bValue === undefined) bValue = '';

                    if (aValue === bValue) {
                        continue;
                    }

                    if (sort.dir === 'asc') {
                        if (typeof aValue === 'number' && typeof bValue === 'number') {
                            return aValue - bValue;
                        }
                        return `${aValue}`.localeCompare(`${bValue}`);
                    } else if (sort.dir === 'desc') {
                        if (typeof aValue === 'number' && typeof bValue === 'number') {
                            return bValue - aValue;
                        }
                        return `${bValue}`.localeCompare(`${aValue}`);
                    }
                }
                return 0;
            })
            .reduce((acc: any[], v: any) => {
                acc.push(v, ...this.attrsList.filter(attrs => attrs.transientParentId === v.transientId));
                return acc;
            }, []);
    }

    displayCheck(row: any) {
        return !row.transientParentId;
    }

    validate() {
        console.debug(`${this.getTitle()} - validate`);

        this.businessAttrs.errors.length = 0;

        if (this.getDuplicateRoadNamesError()) {
            this.businessAttrs.errors.push(this.getDuplicateRoadNamesError());
        }

        this.businessAttrs.errors.push(...this.getFeatureAssociationErrors());

        this.businessAttrs.warnings.length = 0;

        this.businessAttrs.warnings.push(...this.getFeatureAssociationWarnings());
    }

    getParentFeatureCount() {
        return this.getParentAttrsList(this.attrsList).length;
    }

    private getTitle() {
        return `${this.title} - ${this.tool.type}`;
    }

    private getParentAttrsList(attrsList?: any[]) {
        return attrsList?.filter((attrs: any) => !attrs.transientParentId) || [];
    }

    private getChildFeatureCount(parentId: number) {
        return this.getChildAttrsList(parentId).length;
    }

    private getChildAttrsList(parentId: number) {
        return this.features.map(feature => feature.attributes).filter((attrs: any) => attrs.transientParentId === parentId) || [];
    }

    private getDuplicateRoadNamesError() {
        if (this.tool.type === 'road') {
            const duplicateNames = this.spatialUtilService
                .getRoads()
                .map((r: any) => [r.attributes.name, r.attributes.typecode, r.attributes.suffix].filter(v => v).join(' '))
                .filter((v: any, i: number, a: any[]) => a.indexOf(v) !== i)
                .sort()
                .join(', ');

            if (duplicateNames) {
                return `Road names are not unique! Please remove the following duplicated road name(s) via the Information Page: ${duplicateNames}`;
            }
        }
        return '';
    }

    private syncSelectAllCheckbox() {
        if (!this.features) {
            return;
        }
        const allParentIds = this.features.filter(feature => !feature.attributes.transientParentId).map(feature => feature.attributes.transientId);
        const selectedParentIds = this.selectedAttrsList.filter(attrs => !attrs.transientParentId).map(attrs => attrs.transientId);
        const selectAllCheckboxElt: HTMLInputElement = document.querySelector('datatable-header .datatable-checkbox input[type="checkbox"]');
        if (selectAllCheckboxElt) {
            selectAllCheckboxElt.checked = selectedParentIds.length && selectedParentIds.length === allParentIds.length;
        }
    }

    private getFeatureAssociationErrors() {
        if (!this.featuresAssociationActive || this.selectedAttrsList.length === 0 || !['interest', 'lot', 'road'].includes(this.tool.type)) {
            return [];
        }

        const legalParcelTypeLookups: LegalParcelTypeLookup[] = this.lookupService.getLookups('legalParcelType');

        const selectedPolygons: any[] = this.spatialUtilService.getHighlightedPolygons();

        const featureInvalidBusinessClassesMap = {
            interest: ['Other', 'SurveySurround'],
            lot: ['Interest', 'Other', 'SurveySurround'],
            road: ['Interest', 'Other', 'SurveySurround']
        };

        const selectedPolygonsOnRoadWithErrorsMap = selectedPolygons
            .filter(sp =>
                legalParcelTypeLookups.some(
                    l => l.id === sp.attributes.type && featureInvalidBusinessClassesMap[this.tool.type].includes(l.businessClass)
                )
            )
            .reduce((acc, p) => {
                const {id, type} = p.attributes;
                acc[type] = acc[type] || [];
                acc[type].push(id);
                acc[type].sort((a: number, b: number) => a - b);
                return acc;
            }, {});

        if (Object.keys(selectedPolygonsOnRoadWithErrorsMap).length) {
            return Object.entries(selectedPolygonsOnRoadWithErrorsMap).map(([k, v]: any[]) => {
                const description = legalParcelTypeLookups.find(l => l.id === Number(k)).description;
                const connector = ['lot', 'road'].includes(this.tool.type) ? 'a' : 'an';
                return (
                    `${description} polygons cannot be associated with ${connector} ${this.tool.type}.` +
                    ` Please check the following polygon(s): ${v.join()}`
                );
            });
        }

        if (this.tool.type === 'interest') {
            let types: any[];

            const feature = this.spatialUtilService.findById('interest', this.selectedAttrsList[0].id);

            switch (feature.attributes.type) {
                case 1:
                    types = this.lookupService
                        .getLookups('formerTenureNotification')
                        .filter((l: any) => l.notificationCode === feature.attributes.interestcode);
                    break;
                case 2:
                    types = this.lookupService.getLookups('interest').filter((l: any) => l.code === feature.attributes.interestcode);
                    break;
                default:
                    types = [];
                    break;
            }

            const selectedPolylines = this.spatialUtilService.getHighlightedPolylines();
            if (
                selectedPolylines.length &&
                (types.length === 0 ||
                    !selectedPolylines.every(polyline =>
                        types.every(
                            t =>
                                t.scope.includes('POLYLINE') &&
                                (!t.validLineTypeIdsLink ||
                                    t.validLineTypeIdsLink.length === 0 ||
                                    t.validLineTypeIdsLink.some(validLineType => polyline.attributes.type === validLineType))
                        )
                    ))
            ) {
                const errPolyline = this.lookupService
                    .getLookups('featureViewerItem')
                    .find(l => l.view === 'survey' && l.type === 'interest').errorMessage1;
                if (errPolyline) {
                    return [errPolyline];
                }
            }

            if (
                selectedPolygons.length &&
                (types.length === 0 ||
                    !selectedPolygons.every(polygon =>
                        types.every(
                            t =>
                                t.scope.includes('POLYGON') &&
                                (!t.validLegalParcelTypeIdsLink ||
                                    t.validLegalParcelTypeIdsLink.length === 0 ||
                                    t.validLegalParcelTypeIdsLink.some(allowedType => polygon.attributes.type === allowedType))
                        )
                    ))
            ) {
                const errPolygon = this.lookupService
                    .getLookups('featureViewerItem')
                    .find((l: FeatureViewerItemLookup) => l.view === 'survey' && l.type === 'interest').errorMessage2;
                if (errPolygon) {
                    return [errPolygon];
                }
            }

            if (
                selectedPolygons.length &&
                selectedPolylines.length &&
                types.map(i => i.scope).some(scope => scope.includes('POLYGON') && scope.includes('POLYLINE'))
            ) {
                return [
                    'Polygons and polylines (i.e. covenant / restrictive covenant lines) cannot be associated with a 129BA interest at the same' +
                        ' time. Please revise selections.'
                ];
            }
        }

        return [];
    }

    private getFeatureAssociationWarnings() {
        if (!this.featuresAssociationActive || this.selectedAttrsList.length === 0 || !['lot', 'road'].includes(this.tool.type)) {
            return [];
        }

        const legalParcelTypeLookups: LegalParcelTypeLookup[] = this.lookupService.getLookups('legalParcelType');
        const parcelTypeLookups: ParcelTypeLookup[] = this.lookupService.getLookups('parcelType');
        const roadTypeLookups: RoadTypeLookup[] = this.lookupService.getLookups('roadType');
        const selectedPolygons: any[] = this.spatialUtilService.getHighlightedPolygons();

        const selectedBusinessFeatureType = this.selectedAttrsList[0].type;

        switch (this.tool.type) {
            case 'road': {
                const newPolygonTypeToInsert = roadTypeLookups
                    .filter(l => l.description === selectedBusinessFeatureType)
                    .map(l => l.validPolygonTypeCode)[0];

                const legalParcelLookupRoad = legalParcelTypeLookups.find(l => l.id === newPolygonTypeToInsert);
                const legalParcelDescriptionRoad = legalParcelLookupRoad ? legalParcelLookupRoad.description : '';

                const selectedPolygonsOnRoadWithWarnings = selectedPolygons.filter(sp =>
                    legalParcelTypeLookups.some(ptl => ptl.id === sp.attributes.type && ptl.businessClass === 'Lot')
                );

                const selectedPolygonsOnRoadWithWarningsMap = Object.keys(selectedPolygonsOnRoadWithWarnings).reduce((acc, k) => {
                    const type = selectedPolygonsOnRoadWithWarnings[k].attributes.type;
                    acc[type] = acc[type] || [];
                    acc[type].push(selectedPolygonsOnRoadWithWarnings[k].attributes.id);
                    return acc;
                }, {});

                return Object.entries(selectedPolygonsOnRoadWithWarningsMap).map(([key, value]: any[]) => {
                    const parcelType = legalParcelTypeLookups.find(parcelTypeLookup => parcelTypeLookup.id === Number(key));
                    const orderedIds = value.sort((a: number, b: number) => a - b);
                    return (
                        `Proceeding with the association will result in the polygon type being changed from ${parcelType.description}` +
                        ` to ${legalParcelDescriptionRoad} for the following polygon(s): ${orderedIds}`
                    );
                });
            }
            case 'lot': {
                return Object.entries(
                    selectedPolygons
                        .filter(
                            p =>
                                !parcelTypeLookups
                                    .find(l => l.description === selectedBusinessFeatureType)
                                    .validPolygonTypes.split(',')
                                    .map(v => Number(v))
                                    .includes(p.attributes.type) &&
                                legalParcelTypeLookups.find(
                                    lptl =>
                                        lptl.id === p.attributes.type &&
                                        !['Interest', 'Other', 'SurveySurround', 'Undefined'].includes(lptl.businessClass)
                                )
                        )
                        .reduce((acc, v) => {
                            const {id, type} = v.attributes;
                            acc[type] = acc[type] || [];
                            acc[type].push(id);
                            acc[type].sort((a, b) => a - b);
                            return acc;
                        }, {})
                ).map(([k, v]: any[]) => {
                    const parcelType = legalParcelTypeLookups.find(l => l.id === Number(k));
                    const legalParcelTypeDescription = legalParcelTypeLookups.find(
                        lpt => lpt.id === parcelTypeLookups.find(l => l.description === selectedBusinessFeatureType).id
                    ).description;
                    return (
                        `Proceeding with the association will result in the polygon type being changed from ${parcelType.description}` +
                        ` to ${legalParcelTypeDescription} for the following polygon(s): ${v.join()}`
                    );
                });
            }
        }

        return [];
    }
}
